import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, PanelsTopLeft, ShieldCheck, ArrowLeft, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../components/ui/input-otp";
import { AUTH, errMsg } from "./api";

const Lbl = ({ children }) => <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{children}</span>;

const RequestStep = ({ form, setForm, busy, onSubmit }) => (
  <form onSubmit={onSubmit} className="space-y-4" data-testid="otp-request-form">
    <label className="block space-y-1"><Lbl>Your name</Lbl>
      <Input data-testid="login-name-input" required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 border-zinc-800 bg-zinc-900" placeholder="e.g. Rahul Sharma" />
    </label>
    <label className="block space-y-1"><Lbl>Your email</Lbl>
      <Input data-testid="login-email-input" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 border-zinc-800 bg-zinc-900" placeholder="you@company.com" />
    </label>
    <Button data-testid="request-otp-btn" type="submit" disabled={busy} className="h-11 w-full bg-sky-500 text-white hover:bg-sky-600">
      {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}Request OTP
    </Button>
  </form>
);

const VerifyStep = ({ req, code, setCode, busy, onVerify, onBack }) => (
  <div className="space-y-5" data-testid="otp-verify-step">
    <p className="text-sm text-zinc-400" data-testid="otp-sent-note">The OTP has been sent to the administrator at <span className="font-mono text-sky-300">{req.sent_to}</span>. Ask them for the 6-digit code. It's valid for {req.expires_in_min} minutes.</p>
    <InputOTP maxLength={6} value={code} onChange={setCode} onComplete={onVerify} data-testid="otp-input">
      <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-12 border-zinc-700 text-lg" />)}</InputOTPGroup>
    </InputOTP>
    <Button data-testid="verify-otp-btn" disabled={busy || code.length !== 6} onClick={() => onVerify(code)} className="h-11 w-full bg-sky-500 text-white hover:bg-sky-600">
      {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}Verify & sign in
    </Button>
    <button data-testid="otp-back-btn" onClick={onBack} className="flex items-center gap-1.5 text-xs text-zinc-500 transition-colors hover:text-white"><ArrowLeft className="h-3.5 w-3.5" />Change details or request a new OTP</button>
  </div>
);

export default function Login({ onSignedIn }) {
  const [form, setForm] = useState({ name: "", email: "" });
  const [req, setReq] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const request = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${AUTH}/request-otp`, form);
      setReq(data);
      setCode("");
      toast.success("OTP sent to the administrator");
    } catch (err) {
      toast.error(errMsg(err, "Could not request OTP"));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (c) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${AUTH}/verify-otp`, { request_id: req.request_id, code: c }, { withCredentials: true });
      onSignedIn(data.user);
    } catch (err) {
      toast.error(errMsg(err, "Verification failed"));
      setCode("");
      if ([400, 423].includes(err?.response?.status)) setReq(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-[#09090b] px-4 text-zinc-100" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-md border border-sky-500/40 bg-sky-500/10"><PanelsTopLeft className="h-6 w-6 text-sky-400" /></div>
          <div>
            <div className="font-[Manrope] text-lg font-extrabold tracking-tight">FACADE STUDIO</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Envelope Engineering</div>
          </div>
        </div>
        <div className="rounded-xl border border-zinc-800 bg-[#121215] p-6 shadow-2xl">
          <h1 className="font-[Manrope] text-2xl font-extrabold tracking-tight">Sign in</h1>
          <p className="mb-6 mt-1 text-sm text-zinc-500">Access is approved by OTP. The code goes to the administrator, not to you.</p>
          {req ? <VerifyStep req={req} code={code} setCode={setCode} busy={busy} onVerify={verify} onBack={() => setReq(null)} />
            : <RequestStep form={form} setForm={setForm} busy={busy} onSubmit={request} />}
        </div>
      </div>
    </div>
  );
}
